import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, Trash2, UserPlus } from "lucide-react";
import { TopBar } from "@/components/layout/TopBar";
import { CapacityOutlook } from "@/components/capacity/CapacityOutlook";
import { usePortfolio } from "@/hooks/usePortfolio";
import { useRoster, useUpdateMember } from "@/hooks/useRoster";
import {
  useAllAssignments,
  useCreateAssignment,
  useDeleteAssignment,
  type Assignment,
} from "@/hooks/useAssignments";
import { useAuth } from "@/lib/auth";
import { useCan } from "@/lib/roles";
import { byRank } from "@/lib/priorityTier";
import { capacityScopeLabel, countsForCapacity } from "@/lib/capacityScope";
import { useCapacityTiers } from "@/hooks/useCapacityRebuild";
import { cn } from "@/lib/cn";
import type { Project } from "@/types/project";
import type { TeamMember } from "@/types/roster";

/**
 * Capacity — "Staff the Top 10."
 *
 * One page, one truth (rebuilt 2026-09-11, replacing the heatmap
 * dashboard — see git history for the old visuals):
 *
 *   TOP:    the Top 10 in rank order. Each row shows who's on it and
 *           their hrs/wk; unstaffed projects are visually loud. Click
 *           a row to add people and hours right there.
 *   BOTTOM: every capacity person — available hrs/wk (inline
 *           editable), assigned, remaining. Expand a person to see
 *           and edit exactly where their hours go.
 *
 *   OUTLOOK: (2026-09-21) 13 weeks of committed-vs-available per
 *           person, grouped by team, plus "who frees up when" from each
 *           project's go-live. Derived; nothing new to enter.
 *
 * All three read the same project_assignments rows, so the numbers
 * can never disagree. Flat hrs/week, no dating of assignments; a person's
 * "available" is weekly_hrs_available × (1 − support_reserve_pct),
 * both editable here. Viewers see everything read-only.
 */

const fmtH = (v: number) =>
  `${Number.isInteger(v) ? v : v.toFixed(1)}h`;

export function Capacity() {
  const portfolio = usePortfolio(false);
  const rosterQ = useRoster();
  const assignQ = useAllAssignments();
  const { user } = useAuth();
  const canEdit = !!user && user.role !== "viewer";
  // Capacity scope (2026-10-06): only hours on projects in the chosen
  // board bands count — ledger, outlook and Team cards agree.
  const tiers = useCapacityTiers();

  const projects = useMemo(
    () => (Array.isArray(portfolio.data) ? portfolio.data : []),
    [portfolio.data],
  );
  const roster = useMemo(
    () => (Array.isArray(rosterQ.data) ? rosterQ.data : []),
    [rosterQ.data],
  );
  const assignments = useMemo(
    () => (Array.isArray(assignQ.data) ? assignQ.data : []),
    [assignQ.data],
  );

  const top10 = useMemo(
    () =>
      projects
        .filter(
          (p) =>
            p.priority_tier === "top10" &&
            !p.archived_at &&
            p.status !== "complete",
        )
        .sort(byRank),
    [projects],
  );

  const byProject = useMemo(() => {
    const m = new Map<string, Assignment[]>();
    for (const a of assignments) {
      const list = m.get(a.project_id) ?? [];
      list.push(a);
      m.set(a.project_id, list);
    }
    return m;
  }, [assignments]);

  const projectNames = useMemo(() => {
    const m = new Map<string, Project>();
    for (const p of projects) m.set(p.id, p);
    return m;
  }, [projects]);

  const counted = (a: Assignment) => countsForCapacity(projectNames.get(a.project_id), tiers);

  // Every booking per person (the detail rows list them all; the ones
  // outside the scope are marked "not counted").
  const byPerson = useMemo(() => {
    const m = new Map<string, Assignment[]>();
    for (const a of assignments) {
      const list = m.get(a.person_name) ?? [];
      list.push(a);
      m.set(a.person_name, list);
    }
    return m;
  }, [assignments]);

  const people = useMemo(
    () =>
      roster
        .filter((m) => m.include_in_capacity)
        .sort(
          (a, b) =>
            (a.team ?? "zzz").localeCompare(b.team ?? "zzz") ||
            a.name.localeCompare(b.name),
        ),
    [roster],
  );
  const excludedCount = roster.length - people.length;

  const assignedHrs = (list: Assignment[] | undefined) =>
    (list ?? []).reduce((s, a) => s + (a.hours_per_week ?? 0), 0);
  const countedHrs = (list: Assignment[] | undefined) =>
    (list ?? []).filter(counted).reduce((s, a) => s + (a.hours_per_week ?? 0), 0);

  const staffedCount = top10.filter(
    (p) => assignedHrs(byProject.get(p.id)) > 0,
  ).length;
  const top10Hours = top10.reduce(
    (s, p) => s + assignedHrs(byProject.get(p.id)),
    0,
  );
  const totalAvailable = people.reduce(
    (s, m) => s + m.project_capacity_hrs,
    0,
  );
  const totalAssigned = people.reduce(
    (s, m) => s + countedHrs(byPerson.get(m.name)),
    0,
  );
  const scopeLabel = capacityScopeLabel(tiers);

  const loading = portfolio.isLoading || rosterQ.isLoading || assignQ.isLoading;
  const error =
    (portfolio.error ?? rosterQ.error ?? assignQ.error) as Error | null;

  return (
    <>
      <TopBar
        title="Capacity"
        subtitle="Staff the Top 10 · availability and staffing live here"
      />
      <div className="space-y-6 p-8">
        {error && (
          <div className="rounded-xl border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 p-6 text-sm text-red-800 dark:text-red-300">
            {error.message}
          </div>
        )}
        {loading && !error && (
          <div className="rounded-xl border border-line bg-surface p-12 text-center text-sm text-ink-muted">
            Loading capacity…
          </div>
        )}

        {!loading && !error && (
          <>
            {/* One summary line — the page's headline question. */}
            <div
              className="flex flex-wrap items-baseline gap-x-6 gap-y-1 text-sm text-ink-3"
              data-testid="capacity-summary"
            >
              <span>
                <span
                  className={cn(
                    "font-bold tabular-nums",
                    staffedCount === top10.length
                      ? "text-emerald-600 dark:text-emerald-400"
                      : "text-amber-600 dark:text-amber-400",
                  )}
                >
                  {staffedCount} of {top10.length}
                </span>{" "}
                Top 10 staffed
              </span>
              <span>
                <span className="font-bold tabular-nums text-ink">
                  {fmtH(top10Hours)}
                </span>
                /wk on the Top 10
              </span>
              <span>
                <span className="font-bold tabular-nums text-ink">
                  {fmtH(Math.max(0, totalAvailable - totalAssigned))}
                </span>
                /wk still unassigned across the team
              </span>
            </div>

            <Top10Section
              projects={top10}
              byProject={byProject}
              people={people}
              canEdit={canEdit}
            />

            <PeopleSection
              people={people}
              byPerson={byPerson}
              projectNames={projectNames}
              excludedCount={excludedCount}
              canEdit={canEdit}
              tiers={tiers}
            scopeLabel={scopeLabel}
          />

            {/* The time axis (2026-09-21): who's committed where over the
                next quarter, and when hours come free — derived from the
                same assignments + each project's go-live. */}
            <CapacityOutlook people={people} assignments={assignments} projects={projects} countedTiers={tiers} />
          </>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Top half — the Top 10, staffable in place
// ---------------------------------------------------------------------------

function Top10Section({
  projects,
  byProject,
  people,
  canEdit,
}: {
  projects: Project[];
  byProject: Map<string, Assignment[]>;
  people: TeamMember[];
  canEdit: boolean;
}) {
  const [open, setOpen] = useState<string | null>(null);

  return (
    <section className="rounded-xl border border-line bg-surface">
      <div className="border-b border-line-soft px-5 py-3">
        <h2 className="text-sm font-semibold text-ink">Top 10 staffing</h2>
        <p className="text-[11px] text-ink-muted">
          Rank order from the{" "}
          <Link to="/top-projects" className="text-brand-navy-600 dark:text-brand-navy-400 hover:underline">
            Top Projects board
          </Link>
          . Click a project to add people and hours.
        </p>
      </div>
      {projects.length === 0 && (
        <div className="px-5 py-8 text-center text-sm text-ink-muted">
          No Top 10 projects on the board yet.
        </div>
      )}
      <ul className="divide-y divide-line-soft">
        {projects.map((p) => {
          const list = byProject.get(p.id) ?? [];
          const total = list.reduce((s, a) => s + (a.hours_per_week ?? 0), 0);
          const staffed = total > 0;
          const isOpen = open === p.id;
          return (
            <li key={p.id}>
              <button
                onClick={() => setOpen(isOpen ? null : p.id)}
                className="grid w-full grid-cols-[28px_70px_minmax(0,1fr)_auto_90px_16px] items-center gap-3 px-5 py-2.5 text-left transition-colors hover:bg-raised"
              >
                <span className="text-sm font-bold tabular-nums text-ink-subtle dark:text-ink-muted">
                  {p.priority_rank != null ? Math.round(p.priority_rank) : "–"}
                </span>
                <span className="truncate font-mono text-[11px] text-ink-subtle dark:text-ink-muted">
                  {p.id}
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-ink">
                    {p.name}
                  </span>
                  <span className="mt-0.5 flex flex-wrap gap-1">
                    {list
                      .filter((a) => (a.hours_per_week ?? 0) > 0)
                      .map((a) => (
                        <span
                          key={`${a.person_name}::${a.role_key}`}
                          className="rounded-full bg-raised dark:bg-slate-600/70 px-2 py-0.5 text-[10px] font-medium text-ink-3 dark:text-slate-100"
                        >
                          {a.person_name} · {fmtH(a.hours_per_week ?? 0)}
                        </span>
                      ))}
                  </span>
                </span>
                <span
                  className={cn(
                    "rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider",
                    staffed
                      ? "bg-emerald-50 dark:bg-emerald-500/25 text-emerald-700 dark:text-emerald-200"
                      : "bg-amber-50 dark:bg-amber-500/25 text-amber-700 dark:text-amber-200",
                  )}
                >
                  {staffed ? "Staffed" : "Unstaffed"}
                </span>
                <span className="text-right text-sm font-semibold tabular-nums text-ink-2 dark:text-ink">
                  {fmtH(total)}
                  <span className="text-[10px] font-normal text-ink-subtle dark:text-ink-muted">/wk</span>
                </span>
                {isOpen ? (
                  <ChevronDown className="h-4 w-4 text-ink-subtle" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-ink-faint" />
                )}
              </button>
              {isOpen && (
                <ProjectStaffEditor
                  project={p}
                  list={list}
                  people={people}
                  canEdit={canEdit}
                />
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function ProjectStaffEditor({
  project,
  list,
  people,
  canEdit,
}: {
  project: Project;
  list: Assignment[];
  people: TeamMember[];
  canEdit: boolean;
}) {
  const createAssignment = useCreateAssignment();
  const deleteAssignment = useDeleteAssignment();
  const [person, setPerson] = useState("");
  const [hrs, setHrs] = useState("");

  const assignedNames = new Set(list.map((a) => a.person_name));
  const candidates = people.filter((m) => !assignedNames.has(m.name));

  const commitHours = (a: Assignment, raw: string) => {
    const v = Math.max(0, parseFloat(raw) || 0);
    if (v === (a.hours_per_week ?? 0)) return;
    createAssignment.mutate({
      projectId: project.id,
      person_name: a.person_name,
      role_key: a.role_key,
      allocation_pct: 1.0,
      hours_per_week: v,
    });
  };

  const add = () => {
    const member = people.find((m) => m.name === person);
    if (!member) return;
    const v = Math.max(0, parseFloat(hrs) || 0);
    createAssignment.mutate(
      {
        projectId: project.id,
        person_name: member.name,
        role_key: member.role_key,
        allocation_pct: 1.0,
        hours_per_week: v,
      },
      { onSuccess: () => { setPerson(""); setHrs(""); } },
    );
  };

  return (
    <div className="border-t border-line-soft bg-canvas/60 px-5 py-3 pl-[76px]">
      {list.length === 0 && (
        <div className="pb-2 text-xs text-ink-muted">
          No one is on this project yet.
        </div>
      )}
      {list.map((a) => (
        <div
          key={`${a.person_name}::${a.role_key}`}
          className="flex items-center gap-3 py-1"
        >
          <span className="w-56 truncate text-sm text-ink-2">
            {a.person_name}
          </span>
          {canEdit ? (
            <input
              type="number"
              min="0"
              step="0.5"
              defaultValue={a.hours_per_week ?? 0}
              onBlur={(e) => commitHours(a, e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") (e.target as HTMLInputElement).blur();
              }}
              className="w-20 rounded-md border border-line px-2 py-1 text-right text-sm tabular-nums focus:border-brand-navy-400 focus:outline-none"
              aria-label={`Hours per week for ${a.person_name}`}
            />
          ) : (
            <span className="w-20 text-right text-sm tabular-nums text-ink-2">
              {fmtH(a.hours_per_week ?? 0)}
            </span>
          )}
          <span className="text-[10px] text-ink-subtle dark:text-ink-muted">h/wk</span>
          {canEdit && (
            <button
              onClick={() =>
                deleteAssignment.mutate({
                  projectId: project.id,
                  person_name: a.person_name,
                  role_key: a.role_key,
                })
              }
              className="text-ink-faint transition-colors hover:text-red-500"
              title={`Remove ${a.person_name} from ${project.id}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ))}
      {canEdit && (
        <div className="mt-2 flex items-center gap-2 border-t border-line-soft pt-2">
          <UserPlus className="h-3.5 w-3.5 text-ink-subtle" />
          <select
            value={person}
            onChange={(e) => setPerson(e.target.value)}
            className="w-56 rounded-md border border-line bg-surface px-2 py-1 text-sm focus:border-brand-navy-400 focus:outline-none"
            aria-label={`Add person to ${project.id}`}
          >
            <option value="">Add a person…</option>
            {candidates.map((m) => (
              <option key={m.name} value={m.name}>
                {m.name}
                {m.team ? ` — ${m.team}` : ""}
              </option>
            ))}
          </select>
          <input
            type="number"
            min="0"
            step="0.5"
            placeholder="0"
            value={hrs}
            onChange={(e) => setHrs(e.target.value)}
            className="w-20 rounded-md border border-line px-2 py-1 text-right text-sm tabular-nums focus:border-brand-navy-400 focus:outline-none"
            aria-label="Hours per week for new assignment"
          />
          <span className="text-[10px] text-ink-subtle dark:text-ink-muted">h/wk</span>
          <button
            onClick={add}
            disabled={!person || createAssignment.isPending}
            className="rounded-md bg-brand-navy-600 px-3 py-1 text-xs font-medium text-white transition-colors hover:bg-brand-navy-700 disabled:opacity-40"
          >
            Add
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bottom half — the people, availability editable in place
// ---------------------------------------------------------------------------

function PeopleSection({
  people,
  byPerson,
  projectNames,
  excludedCount,
  canEdit,
  tiers,
  scopeLabel,
}: {
  people: TeamMember[];
  byPerson: Map<string, Assignment[]>;
  projectNames: Map<string, Project>;
  excludedCount: number;
  canEdit: boolean;
  tiers: Set<string>;
  scopeLabel: string;
}) {
  const [open, setOpen] = useState<string | null>(null);

  return (
    <section className="rounded-xl border border-line bg-surface" data-testid="people-ledger">
      <div className="border-b border-line-soft px-5 py-3">
        <h2 className="text-sm font-semibold text-ink">People</h2>
        <p className="text-[11px] text-ink-muted">
          Counting hours on <span className="font-medium text-ink-3" data-testid="capacity-scope-label">{scopeLabel}</span> projects (set on Admin). Available = weekly hours minus support reserve. Click a person to
          see and edit where their hours go.
        </p>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[10px] font-semibold uppercase tracking-wider text-ink-subtle">
            <th className="py-2 pl-5 text-left">Name</th>
            <th className="py-2 text-left">Team</th>
            <th className="py-2 text-right">Available</th>
            <th className="py-2 text-right">Assigned</th>
            <th className="py-2 pr-4 text-right">Remaining</th>
            <th className="w-10" />
          </tr>
        </thead>
        <tbody>
          {people.map((m) => (
            <PersonRow
              key={m.name}
              member={m}
              list={byPerson.get(m.name) ?? []}
              projectNames={projectNames}
              tiers={tiers}
              isOpen={open === m.name}
              onToggle={() => setOpen(open === m.name ? null : m.name)}
              canEdit={canEdit}
            />
          ))}
        </tbody>
      </table>
      {excludedCount > 0 && (
        <div className="border-t border-line-soft px-5 py-2 text-[11px] text-ink-subtle">
          {excludedCount} roster {excludedCount === 1 ? "member" : "members"} not
          counted in capacity — manage on{" "}
          <Link to="/roster" className="text-brand-navy-600 dark:text-brand-navy-400 hover:underline">
            Roster
          </Link>
          .
        </div>
      )}
    </section>
  );
}

function PersonRow({
  member,
  list,
  projectNames,
  tiers,
  isOpen,
  onToggle,
  canEdit,
}: {
  member: TeamMember;
  list: Assignment[];
  projectNames: Map<string, Project>;
  tiers: Set<string>;
  isOpen: boolean;
  onToggle: () => void;
  canEdit: boolean;
}) {
  const createAssignment = useCreateAssignment();
  const deleteAssignment = useDeleteAssignment();
  const [editingAvail, setEditingAvail] = useState(false);
  // Availability (weekly hours, support reserve) is a roster number —
  // PMO only (2026-09-28). Assigning hours to a project stays a User edit.
  const { isPmo: canEditAvailability } = useCan();

  // Only in-scope bands count (Admin → Capacity scope); other bookings
  // still list below, marked "not counted".
  const inScope = (a: Assignment) => countsForCapacity(projectNames.get(a.project_id), tiers);
  const assigned = list.filter(inScope).reduce((s, a) => s + (a.hours_per_week ?? 0), 0);
  const available = member.project_capacity_hrs;
  const remaining = available - assigned;
  const withHours = list.filter((a) => (a.hours_per_week ?? 0) > 0);

  return (
    <>
      <tr
        className="cursor-pointer border-t border-line-soft transition-colors hover:bg-raised"
        onClick={onToggle}
      >
        <td className="py-2 pl-5 font-medium text-ink">{member.name}</td>
        <td className="py-2 text-ink-muted">{member.team ?? "—"}</td>
        <td
          className="py-2 text-right tabular-nums text-ink-2"
          onClick={(e) => e.stopPropagation()}
        >
          {canEditAvailability && editingAvail ? (
            <AvailabilityEditor
              member={member}
              onDone={() => setEditingAvail(false)}
            />
          ) : (
            <button
              onClick={() => canEditAvailability && setEditingAvail(true)}
              className={cn(
                "rounded px-1 tabular-nums",
                canEditAvailability &&
                  "cursor-pointer underline decoration-dotted decoration-ink-faint underline-offset-4 hover:bg-raised",
              )}
              title={
                canEditAvailability
                  ? `Edit availability (works ${fmtH(member.weekly_hrs_available)}/wk, ${Math.round(member.support_reserve_pct * 100)}% support reserve)`
                  : undefined
              }
            >
              {fmtH(available)}
            </button>
          )}
        </td>
        <td className="py-2 text-right tabular-nums text-ink-2">
          {fmtH(assigned)}
        </td>
        <td
          className={cn(
            "py-2 pr-4 text-right font-semibold tabular-nums",
            remaining < 0
              ? "text-red-600 dark:text-red-400"
              : remaining === 0
                ? "text-ink-subtle"
                : "text-emerald-600 dark:text-emerald-400",
          )}
        >
          {fmtH(remaining)}
        </td>
        <td className="pr-4 text-right">
          {isOpen ? (
            <ChevronDown className="ml-auto h-4 w-4 text-ink-subtle" />
          ) : (
            <ChevronRight className="ml-auto h-4 w-4 text-ink-faint" />
          )}
        </td>
      </tr>
      {isOpen && (
        <tr className="border-t border-line-soft bg-canvas/60">
          <td colSpan={6} className="px-5 py-3 pl-10">
            {withHours.length === 0 && (
              <div className="text-xs text-ink-muted">
                No hours assigned — {member.name} is fully free.
              </div>
            )}
            {withHours.map((a) => {
              const proj = projectNames.get(a.project_id);
              return (
                <div
                  key={`${a.project_id}::${a.role_key}`}
                  className="flex items-center gap-3 py-1"
                >
                  <Link
                    to={`/portfolio/${a.project_id}`}
                    className="w-72 truncate text-sm text-brand-navy-700 dark:text-brand-navy-300 hover:underline"
                  >
                    {proj?.name ?? a.project_id}
                  </Link>
                  {proj?.priority_tier === "top10" && (
                    <span className="rounded-full bg-brand-navy-100 dark:bg-brand-navy-500/30 px-2 py-0.5 text-[10px] font-semibold text-brand-navy-700 dark:text-brand-navy-200">
                      Top 10
                    </span>
                  )}
                  {!inScope(a) && (
                    <span className="rounded-full bg-raised px-2 py-0.5 text-[10px] font-medium text-ink-muted" title="This project's band doesn't count toward capacity (Admin → Capacity scope)" data-testid="not-counted">
                      not counted
                    </span>
                  )}
                  {canEdit ? (
                    <input
                      type="number"
                      min="0"
                      step="0.5"
                      defaultValue={a.hours_per_week ?? 0}
                      onBlur={(e) => {
                        const v = Math.max(0, parseFloat(e.target.value) || 0);
                        if (v === (a.hours_per_week ?? 0)) return;
                        createAssignment.mutate({
                          projectId: a.project_id,
                          person_name: a.person_name,
                          role_key: a.role_key,
                          allocation_pct: 1.0,
                          hours_per_week: v,
                        });
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter")
                          (e.target as HTMLInputElement).blur();
                      }}
                      className="w-20 rounded-md border border-line px-2 py-1 text-right text-sm tabular-nums focus:border-brand-navy-400 focus:outline-none"
                      aria-label={`Hours per week on ${a.project_id}`}
                    />
                  ) : (
                    <span className="w-20 text-right text-sm tabular-nums">
                      {fmtH(a.hours_per_week ?? 0)}
                    </span>
                  )}
                  <span className="text-[10px] text-ink-subtle dark:text-ink-muted">h/wk</span>
                  {canEdit && (
                    <button
                      onClick={() =>
                        deleteAssignment.mutate({
                          projectId: a.project_id,
                          person_name: a.person_name,
                          role_key: a.role_key,
                        })
                      }
                      className="text-ink-faint transition-colors hover:text-red-500"
                      title={`Remove ${member.name} from ${a.project_id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              );
            })}
          </td>
        </tr>
      )}
    </>
  );
}

/** Inline editor for "works X hrs/wk, minus Y% support reserve" — the two
 *  numbers behind the Available column. Saves via the full roster PUT. */
function AvailabilityEditor({
  member,
  onDone,
}: {
  member: TeamMember;
  onDone: () => void;
}) {
  const updateMember = useUpdateMember();
  const [weekly, setWeekly] = useState(String(member.weekly_hrs_available));
  const [reserve, setReserve] = useState(
    String(Math.round(member.support_reserve_pct * 100)),
  );

  const w = Math.max(0, parseFloat(weekly) || 0);
  const r = Math.min(100, Math.max(0, parseFloat(reserve) || 0));
  const effective = w * (1 - r / 100);

  const save = () => {
    updateMember.mutate(
      {
        originalName: member.name,
        payload: {
          name: member.name,
          role: member.role,
          role_key: member.role_key,
          team: member.team,
          vendor: member.vendor,
          classification: member.classification,
          rate_per_hour: member.rate_per_hour,
          weekly_hrs_available: w,
          support_reserve_pct: r / 100,
          include_in_capacity: member.include_in_capacity,
        },
      },
      { onSuccess: onDone },
    );
  };

  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs">
      <input
        type="number"
        min="0"
        value={weekly}
        onChange={(e) => setWeekly(e.target.value)}
        className="w-16 rounded-md border border-line px-1.5 py-0.5 text-right tabular-nums focus:border-brand-navy-400 focus:outline-none"
        aria-label={`Weekly hours for ${member.name}`}
        autoFocus
      />
      <span className="text-ink-subtle">hrs −</span>
      <input
        type="number"
        min="0"
        max="100"
        value={reserve}
        onChange={(e) => setReserve(e.target.value)}
        className="w-14 rounded-md border border-line px-1.5 py-0.5 text-right tabular-nums focus:border-brand-navy-400 focus:outline-none"
        aria-label={`Support reserve % for ${member.name}`}
      />
      <span className="text-ink-subtle">% = {fmtH(effective)}</span>
      <button
        onClick={save}
        disabled={updateMember.isPending}
        className="rounded-md bg-brand-navy-600 px-2 py-0.5 font-medium text-white hover:bg-brand-navy-700 disabled:opacity-40"
      >
        Save
      </button>
      <button onClick={onDone} className="text-ink-subtle hover:text-ink-3">
        Cancel
      </button>
    </span>
  );
}
